import { AuthenticatedTemplate, UnauthenticatedTemplate, useIsAuthenticated, useMsal } from "@azure/msal-react";
import { InteractionStatus } from "@azure/msal-browser";
import { useEffect, useMemo } from "react";
import { Routes, Route, Navigate } from "react-router-dom";
import { Toaster } from "sonner";
import { Header } from "./components";
import { AppSidebar } from "./components/app-sidebar";
import { Button } from "./components/ui/button";
import { SidebarInset, SidebarProvider } from "./components/ui/sidebar";
import { MicrosoftLogo } from "./icons";
import { RequireAllocationsAccess } from "./components/RequireAllocationsAccess";
import { Allocations } from "./pages/Allocations";
import { Pipeline } from "./pages/Pipeline";
import { setPhoenixToken } from "./services/apiClient";
import { AuthService } from "./services/AuthService";
import { isOfflineMode } from "./helpers/offlineMode";

function AuthenticatedContent() {
  const isProduction = import.meta.env.MODE === "production";
  const headerHeightClass = isProduction
    ? "[--header-height:calc(var(--spacing)*14)]"
    : "[--header-height:calc(var(--spacing)*22)] 2xl:[--header-height:calc(var(--spacing)*28)]";

  return (
    <div className={headerHeightClass}>
      <SidebarProvider open={false} onOpenChange={() => {}} className="flex flex-col">
        <Header />
        <div className="flex flex-1 pt-(--header-height)">
          <AppSidebar />
          <SidebarInset>
            <Routes>
              <Route path="/" element={<Navigate to="/pipeline" replace />} />
              <Route path="/pipeline" element={<Pipeline />} />
              <Route
                path="/allocations"
                element={
                  <RequireAllocationsAccess>
                    <Allocations />
                  </RequireAllocationsAccess>
                }
              />
            </Routes>
          </SidebarInset>
        </div>
      </SidebarProvider>
    </div>
  );
}

function OnlineApp() {
  const { instance, inProgress } = useMsal();
  const isAuthenticated = useIsAuthenticated();
  const authService = useMemo(() => new AuthService(), []);

  useEffect(() => {
    if (!isAuthenticated) return;
    const exchangeToken = async () => {
      try {
        const { phoenixToken } = await authService.authenticate(instance);
        setPhoenixToken(phoenixToken.authenticationToken);
      } catch (error) {
        console.error("Failed to exchange token:", error);
      }
    };

    exchangeToken();
  }, [isAuthenticated, instance, authService]);

  const handleLogin = () => {
    instance.loginRedirect({
      scopes: ["User.Read"],
    });
  };

  useEffect(() => {
    if (!isAuthenticated && inProgress === InteractionStatus.None) {
      handleLogin();
    }
  }, [isAuthenticated, inProgress, instance]);

  return (
    <>
      <AuthenticatedTemplate>
        <AuthenticatedContent />
      </AuthenticatedTemplate>

      <UnauthenticatedTemplate>
        <div className="bg-primary-200 flex h-screen w-full items-center justify-center">
          <Button onClick={handleLogin}>
            <MicrosoftLogo />
            Sign in with Microsoft
          </Button>
        </div>
      </UnauthenticatedTemplate>
    </>
  );
}

function OfflineApp() {
  return <AuthenticatedContent />;
}

function App() {
  return (
    <>
      <Toaster position="bottom-right" richColors />
      {isOfflineMode() ? <OfflineApp /> : <OnlineApp />}
    </>
  );
}

export default App;
